import React from 'react'
import Layout from '../components/layout/Layout'
import { UserContextProvider } from '../utils/hooks/useUserContext'
import { Navigate, Route, Routes, useParams } from 'react-router-dom'
import { ThemeContext } from '../utils/providers/themeContext'
import {
  AdvertiserRouteUrl,
  AdGroupRouteUrl,
  AllRoute,
  CampaignRouteUrl,
  AgencyRouteUrl,
  PositionRouteUrl,
  WebsiteRouteUrl,
  PublisherRouteUrl,
  TradingDeskRouteUrl,
  InventoryRouteUrl,
  CampaignProgrammaticRouteUrl,
  ActivityRouteUrl,
  ResellerRouteUrl,
  ExternalAdGroupLinkUrl,
} from './routeParams'
import { Box } from '@mui/material'
import { ConfirmDialogContextProvider } from '../utils/providers/confirmDialogContext'
import CampaignProgrammatic from './programmaticCampaign/dashboard/Dashboard'
import Inventory from './inventory/dashboard/Dashboard'
import Activity from './activity/Activity'
import { CurrentCompanyDisplayedProvider } from './company/currentCompanyDisplayedContext'
import { CompanyDetailsContextProvider } from '../utils/hooks/useCompanyDetailsContext'
import Loader from '../components/loader/Loader'
import { AdvertiserConfigContextProvider } from '../utils/hooks/advertiser/useAdvertiserConfigContext'
import { PublisherConfigContextProvider } from '../utils/hooks/publisher/usePublisherConfigContext'
import { useCguRedirection } from '../utils/hooks/company/useCguCheck'
import AdGroupToFullLinkRedirect from './adgroup/AdGroupToFullLinkRedirect'
import { TabNavContextProvider } from '../utils/providers/tabNavContext'
import { HeaderColorContextProvider } from '../utils/providers/headerColorContext'
import { SidebarContextProvider, useRegisterSidebar } from '../utils/providers/sidebarContext'

const Home = React.lazy(() => import('./home/Home'))
const NotFound = React.lazy(() => import('./NotFound'))
const Website = React.lazy(() => import('./website/Website'))
const Position = React.lazy(() => import('./position/Position'))
const Campaign = React.lazy(() => import('./campaign/Campaign'))
const Adgroup = React.lazy(() => import('./adgroup/Adgroup'))
const Agency = React.lazy(() => import('./agency/Agency'))
const Publisher = React.lazy(() => import('./publisher/Publisher'))
const Advertiser = React.lazy(() => import('./advertiser/Advertiser'))
const TradingDesk = React.lazy(() => import('./tradingDesk/TradingDesk'))
const Reseller = React.lazy(() => import('./reseller/Reseller'))

interface LayoutWithSidebarProps {
  withCompanyDetails?: boolean
  children: React.ReactNode
}

const LayoutWithSidebar: React.FC<LayoutWithSidebarProps> = ({ children, withCompanyDetails = false }) => {
  useCguRedirection()
  const { slug = '' } = useParams<AllRoute>()
  useRegisterSidebar(slug)

  const content = withCompanyDetails ? (
    <CompanyDetailsContextProvider companySlug={slug}>{children}</CompanyDetailsContextProvider>
  ) : (
    children
  )

  return <CurrentCompanyDisplayedProvider>{content}</CurrentCompanyDisplayedProvider>
}

const LayoutWithoutSidebar: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useCguRedirection()

  return <CurrentCompanyDisplayedProvider>{children}</CurrentCompanyDisplayedProvider>
}

const AppRouter = (): JSX.Element => (
  <UserContextProvider>
    <ConfirmDialogContextProvider>
      <ThemeContext>
        <HeaderColorContextProvider>
          <TabNavContextProvider>
            <SidebarContextProvider>
              <Layout>
                <Box
                  sx={{
                    display: 'flex',
                    flexGrow: 1,
                    flexDirection: 'row',
                    overflow: 'hidden',
                    height: '100%',
                    minHeight: '100%',
                    backgroundColor: (theme) => theme.palette.background.default,
                  }}
                >
                  <Routes>
                    <Route
                      path={ExternalAdGroupLinkUrl}
                      element={
                        <React.Suspense fallback={<Loader />}>
                          <AdGroupToFullLinkRedirect />
                        </React.Suspense>
                      }
                    />

                    <Route
                      path={`${ActivityRouteUrl}/*`}
                      element={
                        <LayoutWithSidebar withCompanyDetails>
                          <React.Suspense fallback={<Loader />}>
                            <Activity />
                          </React.Suspense>
                        </LayoutWithSidebar>
                      }
                    />
                    <Route
                      path={`${WebsiteRouteUrl}/*`}
                      element={
                        <LayoutWithSidebar>
                          <PublisherConfigContextProvider>
                            <React.Suspense fallback={<Loader />}>
                              <Website />
                            </React.Suspense>
                          </PublisherConfigContextProvider>
                        </LayoutWithSidebar>
                      }
                    />
                    <Route
                      path={`${PositionRouteUrl}/*`}
                      element={
                        <LayoutWithSidebar>
                          <PublisherConfigContextProvider>
                            <React.Suspense fallback={<Loader />}>
                              <Position />
                            </React.Suspense>
                          </PublisherConfigContextProvider>
                        </LayoutWithSidebar>
                      }
                    />
                    <Route
                      path={`${PublisherRouteUrl}/*`}
                      element={
                        <LayoutWithSidebar>
                          <PublisherConfigContextProvider>
                            <React.Suspense fallback={<Loader />}>
                              <Publisher />
                            </React.Suspense>
                          </PublisherConfigContextProvider>
                        </LayoutWithSidebar>
                      }
                    />
                    <Route
                      path={`${CampaignProgrammaticRouteUrl}/*`}
                      element={
                        <LayoutWithSidebar>
                          <React.Suspense fallback={<Loader />}>
                            <CampaignProgrammatic />
                          </React.Suspense>
                        </LayoutWithSidebar>
                      }
                    />
                    <Route
                      path={`${CampaignRouteUrl}/*`}
                      element={
                        <LayoutWithSidebar>
                          <AdvertiserConfigContextProvider>
                            <React.Suspense fallback={<Loader />}>
                              <Campaign />
                            </React.Suspense>
                          </AdvertiserConfigContextProvider>
                        </LayoutWithSidebar>
                      }
                    />
                    <Route
                      path={`${InventoryRouteUrl}/*`}
                      element={
                        <LayoutWithSidebar>
                          <React.Suspense fallback={<Loader />}>
                            <Inventory />
                          </React.Suspense>
                        </LayoutWithSidebar>
                      }
                    />
                    <Route
                      path={`${AdGroupRouteUrl}/*`}
                      element={
                        <LayoutWithSidebar>
                          <AdvertiserConfigContextProvider>
                            <React.Suspense fallback={<Loader />}>
                              <Adgroup />
                            </React.Suspense>
                          </AdvertiserConfigContextProvider>
                        </LayoutWithSidebar>
                      }
                    />
                    <Route
                      path={`${AdvertiserRouteUrl}/*`}
                      element={
                        <LayoutWithSidebar>
                          <AdvertiserConfigContextProvider>
                            <React.Suspense fallback={<Loader />}>
                              <Advertiser />
                            </React.Suspense>
                          </AdvertiserConfigContextProvider>
                        </LayoutWithSidebar>
                      }
                    />
                    <Route
                      path={`${TradingDeskRouteUrl}/*`}
                      element={
                        <LayoutWithSidebar>
                          <React.Suspense fallback={<Loader />}>
                            <TradingDesk />
                          </React.Suspense>
                        </LayoutWithSidebar>
                      }
                    />
                    <Route
                      path={`${AgencyRouteUrl}/*`}
                      element={
                        <LayoutWithSidebar>
                          <React.Suspense fallback={<Loader />}>
                            <Agency />
                          </React.Suspense>
                        </LayoutWithSidebar>
                      }
                    />

                    <Route
                      path="/home/*"
                      element={
                        <React.Suspense fallback={<Loader />}>
                          <Home />
                        </React.Suspense>
                      }
                    />

                    <Route
                      path={`${ResellerRouteUrl}/*`}
                      element={
                        <LayoutWithoutSidebar>
                          <React.Suspense fallback={<Loader />}>
                            <Reseller />
                          </React.Suspense>
                        </LayoutWithoutSidebar>
                      }
                    />
                    <Route path="/" element={<Navigate to="/home" replace />} />
                    <Route path="/login" element={<Navigate to="/home" replace />} />
                    <Route
                      path="*"
                      element={
                        <React.Suspense fallback={<Loader />}>
                          <NotFound />
                        </React.Suspense>
                      }
                    />
                  </Routes>
                </Box>
              </Layout>
            </SidebarContextProvider>
          </TabNavContextProvider>
        </HeaderColorContextProvider>
      </ThemeContext>
    </ConfirmDialogContextProvider>
  </UserContextProvider>
)

export default AppRouter
